import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import logo from '../Images/logo-main-black.png';
import signature from '../Images/signature.png';
import { app } from '../firebase';
import { getFirestore, collection, query, where, doc, onSnapshot } from 'firebase/firestore';

const normalizeTimestamp = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'number') return value;

  const parsedValue = new Date(value).getTime();
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatDate = (value, fallback = 'Not set') => {
  const timestamp = normalizeTimestamp(value);
  if (!timestamp) return fallback;

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
  }).format(new Date(timestamp));
};

const normalizeText = (value) => String(value || '').trim();

const sanitizeFileName = (value) => {
  const safeValue = normalizeText(value)
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '');

  return safeValue || 'student';
};

const resolveStudentTraineeOnboardedAt = (traineeRecord, userRecord) => {
  return (
    userRecord?.studentTraineeOnboardedAt ||
    traineeRecord?.onboardedAt ||
    traineeRecord?.createdAt ||
    userRecord?.studentTraineeUpdatedAt ||
    null
  );
};

const resolveStudentTraineeCompensation = (traineeRecord, userRecord) => {
  return (
    userRecord?.traineeCompensation ||
    userRecord?.traineeCompensationExpectation ||
    userRecord?.traineeCompensationAmount ||
    traineeRecord?.compensation ||
    traineeRecord?.compensationExpectation ||
    traineeRecord?.compensationAmount ||
    'Not set'
  );
};

function StudentTraineeLetterComp() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get('userId') || '';
  const letterRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadState, setLoadState] = useState({
    status: '',
    reason: '',
  });
  const [userRecord, setUserRecord] = useState(null);
  const [traineeRecord, setTraineeRecord] = useState(null);

  useEffect(() => {
    if (!userId) {
      setLoadState({ status: '', reason: 'missing_user' });
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);

    const db = getFirestore(app);
    const userQuery = query(collection(db, 'users'), where('userId', '==', userId));
    let hasReceivedUserSnapshot = false;
    let hasReceivedTraineeSnapshot = false;

    const finishInitialLoad = () => {
      if (hasReceivedUserSnapshot && hasReceivedTraineeSnapshot) {
        setIsLoading(false);
      }
    };

    const userUnsubscribe = onSnapshot(
      userQuery,
      (snapshot) => {
        hasReceivedUserSnapshot = true;
        const nextUserRecord = snapshot.docs[0]?.data() || null;
        setUserRecord(nextUserRecord);
        setLoadState((previousState) => ({
          ...previousState,
          status: nextUserRecord?.userStatus || '',
        }));
        finishInitialLoad();
      },
      (error) => {
        console.error('Error loading trainee placement letter user record:', error);
        hasReceivedUserSnapshot = true;
        setLoadState({ status: '', reason: 'error' });
        setIsLoading(false);
      }
    );

    const traineeUnsubscribe = onSnapshot(
      doc(db, 'studentTrainees', userId),
      (snapshot) => {
        hasReceivedTraineeSnapshot = true;
        const nextTraineeRecord = snapshot.exists() ? snapshot.data() : null;
        setTraineeRecord(nextTraineeRecord);
        setLoadState((previousState) => ({
          ...previousState,
          reason: nextTraineeRecord ? '' : 'missing_trainee_record',
        }));
        finishInitialLoad();
      },
      (error) => {
        console.error('Error loading trainee placement letter trainee record:', error);
        hasReceivedTraineeSnapshot = true;
        setLoadState({ status: '', reason: 'error' });
        setIsLoading(false);
      }
    );

    return () => {
      userUnsubscribe();
      traineeUnsubscribe();
    };
  }, [userId]);

  const traineeSummary = useMemo(() => {
    if (!userRecord || !traineeRecord) return null;

    return {
      fullName: traineeRecord.fullName || userRecord.bio || 'Student',
      course: traineeRecord.course || userRecord.course || 'Not set',
      cohort: userRecord.cohort || traineeRecord.cohort || 'Not set',
      traineeArea: traineeRecord.traineeArea || traineeRecord.traineeTrack || 'Not set',
      availability: traineeRecord.availability || 'Not set',
      hoursPerDay: traineeRecord.hoursPerDay || traineeRecord.hoursPerWeek || 'Not set',
      preferredTerm: traineeRecord.preferredTerm || traineeRecord.termPreference || 'Not set',
      compensation: resolveStudentTraineeCompensation(traineeRecord, userRecord),
      onboardedAt: resolveStudentTraineeOnboardedAt(traineeRecord, userRecord),
      updatedAt: traineeRecord.updatedAt || null,
      admissionNumber: traineeRecord.admissionNumber || userRecord.admissionNumber || userRecord.studentId || 'Not set',
    };
  }, [userRecord, traineeRecord]);

  const handleBack = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  const handleDownloadPdf = async () => {
    if (!letterRef.current || !traineeSummary) return;

    setIsDownloading(true);

    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      const images = letterRef.current.querySelectorAll('img');
      await Promise.all(
        Array.from(images).map((img) => (
          img.complete ? Promise.resolve() : new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          })
        ))
      );

      const canvas = await html2canvas(letterRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pdfWidth = 210;
      const pdfHeight = 297;
      const imgAspect = canvas.width / canvas.height;
      let renderWidth = pdfWidth - 20;
      let renderHeight = renderWidth / imgAspect;

      if (renderHeight > pdfHeight - 20) {
        renderHeight = pdfHeight - 20;
        renderWidth = renderHeight * imgAspect;
      }

      const offsetX = (pdfWidth - renderWidth) / 2;
      const offsetY = 10;

      pdf.addImage(imgData, 'JPEG', offsetX, offsetY, renderWidth, renderHeight, undefined, 'FAST');
      pdf.save(`Student_Trainee_Placement_Letter_${sanitizeFileName(traineeSummary.fullName)}.pdf`);
    } catch (error) {
      console.error('Error exporting trainee placement letter:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="traineeLetterPage">
        <div className="traineeLetterShell">
          <div className="traineeLetterStateCard">
            <h2>Loading placement letter...</h2>
            <p>Please wait while we prepare the student trainee placement letter.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!traineeSummary) {
    return (
      <div className="traineeLetterPage">
        <div className="traineeLetterToolbar">
          <button className="btn btn-outline-dark" onClick={handleBack}>Back</button>
        </div>
        <div className="traineeLetterShell">
          <div className="traineeLetterStateCard">
            <h2>Placement Letter Unavailable</h2>
            <p>
              {loadState.reason === 'missing_trainee_record'
                ? 'This student has not been onboarded as a trainee yet.'
                : 'We could not load the trainee placement letter right now.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="traineeLetterPage">
      <div className="traineeLetterToolbar">
        <button className="btn btn-outline-dark" onClick={handleBack}>Back</button>
        <button className="btn btn-dark" onClick={handleDownloadPdf} disabled={isDownloading}>
          {isDownloading ? 'Preparing PDF...' : 'Download PDF'}
        </button>
      </div>

      <div className="traineeLetterShell">
        <div className="traineeLetterDocument traineeLetterDocumentAdmin" ref={letterRef}>
          <div className='traineeLetterHeader'>
            <img src={logo} alt='Ubunifu College' className='traineeLetterLogo' />
            <div className='traineeLetterHeaderCopy'>
              <p>UBUNIFU COLLEGE</p>
              <span>Developing the capacity to innovate</span>
            </div>
          </div>

          <div className='traineeLetterBody'>
            <div className='traineeLetterTopRow'>
              <span>Date: {formatDate(traineeSummary.onboardedAt || traineeSummary.updatedAt)}</span>
              <span>Ref: Student Trainee Program</span>
            </div>

            <h1>Student Trainee Placement Letter</h1>

            <p>Dear {traineeSummary.fullName},</p>

            <p>
              This letter confirms your onboarding into the Ubunifu College Student Trainee Program following your completion
              of studies. The details of your current placement are outlined below.
            </p>

            <p>
              Your trainee placement is under the <strong>{traineeSummary.traineeArea}</strong> area. You completed the
              <strong> {traineeSummary.course}</strong> programme under the <strong>{traineeSummary.cohort}</strong> cohort,
              with admission number <strong>{traineeSummary.admissionNumber}</strong>.
            </p>

            <p>
              For the <strong>{traineeSummary.preferredTerm}</strong> term, your recorded availability is
              <strong> {traineeSummary.availability}</strong>, with an expected commitment of
              <strong> {traineeSummary.hoursPerDay}</strong> per day. The recorded compensation for this trainee placement is
              <strong> {traineeSummary.compensation}</strong> per month. This is the expected monthly amount, though it may
              be paid weekly, every two weeks, or monthly depending on the financial capacity of the school or based on how
              assigned clients or students make their payments. This compensation may also be reviewed and increased based
              on good performance during the trainee placement period.
            </p>

            <p>
              Please keep this letter for your records. It confirms your placement within the student trainee program and
              clarifies the current trainee terms recorded by the college.
            </p>

            <p className='traineeLetterDisclaimer'>
              This placement letter is issued for the student trainee program and does not constitute an employment contract.
            </p>

            <div className='traineeLetterSignatureBlock'>
              <img src={signature} alt='Signature' className='traineeLetterSignature' />
              <div className='traineeLetterSignatureLine' />
              <strong>REINHARD OMUKUBA</strong>
              <span>DIRECTOR</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentTraineeLetterComp;
